import { Box } from '@mui/material';
import { forwardRef, ReactNode, useEffect, useMemo } from 'react';

import {
  useAppDispatch,
  useSelectWidgetSettingsWithSharing,
  widgetInstanceActions,
} from '#features';
import { type ControlsProps } from '#shared';

import { widgetClasses, WidgetStyled } from './WidgetStyled';
import { WidgetToolbar } from './WidgetToolbar';

import type { UnknownSettings, WidgetProps } from '#features';

const buildDefaults = (
  cfg?: Record<string, ControlsProps>,
  props?: WidgetProps,
) => {
  if (!cfg) {
    return {};
  }
  const out: Record<string, ControlsProps['value']> = {};
  for (const [k, c] of Object.entries(cfg)) {
    out[k] = props?.[k] !== undefined ? props[k] : c.defaultValue;
  }
  return out;
};
export const Widget = forwardRef<HTMLDivElement, WidgetProps>((props, ref) => {
  const {
    children,
    instance,
    config,
    showSettingsButton = true,
    outlined,
    className,
    style,
    ...rest
  } = props;
  const dispatch = useAppDispatch();
  const defaults = useMemo(
    () => buildDefaults(config as Record<string, ControlsProps>, props),
    [config, props],
  );

  // Get either shared settings or instance's own settings
  const sharedSettings = useSelectWidgetSettingsWithSharing(instance);

  // precedence: props.settings > redux settings > {}
  const resolvedSettings = useMemo<Record<string, ControlsProps['value']>>(
    () => ({
      ...defaults,
      ...((sharedSettings ?? {}) as Record<string, ControlsProps['value']>),
    }),
    [defaults, sharedSettings],
  );

  const content =
    typeof children === 'function'
      ? (children as (s: UnknownSettings) => ReactNode)(resolvedSettings)
      : children;

  //Toggle visibility in store on mount/unmount
  useEffect(() => {
    if (!instance?.id) {
      return;
    }
    dispatch(
      widgetInstanceActions.setWidgetInstanceVisible({
        id: instance.id,
        visible: true,
      }),
    );
    return () => {
      dispatch(
        widgetInstanceActions.setWidgetInstanceVisible({
          id: instance.id,
          visible: false,
        }),
      );
    };
  }, [dispatch, instance.id]);

  return (
    <WidgetStyled
      id={instance?.id}
      ref={ref}
      className={`${widgetClasses.root} ${className ?? ''}`}
      outlined={outlined}
      style={style}
      {...rest}
    >
      {instance?.id && config && showSettingsButton && (
        <WidgetToolbar instanceId={instance.id} config={config} />
      )}

      <Box className={widgetClasses.container}>{content}</Box>
    </WidgetStyled>
  );
});

Widget.displayName = 'Widget';
